import { useNavigate } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { useGeolocated } from "react-geolocated";
import { fetchNearbyRestaurants } from "../api/apis";

export default function RestaurantPage() {
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const {
        coords,
        isGeolocationAvailable,
        isGeolocationEnabled,
        positionError,
    } = useGeolocated({
        positionOptions: {
            enableHighAccuracy: false,
            timeout: 8000,
            maximumAge: 60_000,
        },
        userDecisionTimeout: 8000,
        watchPosition: false,
    });

    // Build a simple UI status
    const locationStatus = useMemo(() => {
        if (!isGeolocationAvailable) {
            return { type: "warning", text: "Din webbläsare stöder inte platsdata." };
        }
        if (positionError) {
            // If permission denied, be explicit about enabling
            const denied = positionError.code === 1;
            return {
                type: "warning",
                text: denied
                    ? "Tillåt platsdata för att se närliggande restauranger."
                    : `Kunde inte hämta platsdata: ${positionError.message}`,
            };
        }
        if (!isGeolocationEnabled) {
            return {
                type: "info",
                text: "Tillåt platsdata för att se närliggande restauranger.",
            };
        }
        if (!coords) {
            return { type: "info", text: "Hämtar din plats…" };
        }
        return null;
    }, [isGeolocationAvailable, isGeolocationEnabled, positionError, coords]);

    useEffect(() => {
        const run = async () => {
            if (!coords) return;
            try {
                setIsLoading(true);
                setFetchError(null);
                const res = await fetchNearbyRestaurants({
                    lat: coords.latitude,
                    lon: coords.longitude,
                });
                setRestaurants(Array.isArray(res?.data) ? res.data : []);
            } catch (err) {
                setFetchError("Kunde inte hämta restauranger just nu.");
            } finally {
                setIsLoading(false);
            }
        };
        run();
    }, [coords]);

    const showEmpty =
        !isLoading &&
        !fetchError &&
        coords &&
        Array.isArray(restaurants) &&
        restaurants.length === 0;

    return (
        <div className="container text-center mt-4">
            <h2 className="mb-2 fw-semibold">Restauranger nära dig</h2>

            {/* Context hint for how user got here */}
            <p className="text-muted mb-4">
                Om du kom från en QR-kod, prova att scanna igen
            </p>

            {/* Location / permission messages */}
            {locationStatus && (
                <div
                    className={`alert ${
                        locationStatus.type === "warning"
                            ? "alert-primary primaryText"
                            : "alert-info"
                    } mx-auto`}
                    style={{ maxWidth: 520 }}
                    role="alert"
                >
                    {locationStatus.text}
                    <div className="mt-2 small text-muted">
                        Tips: Aktivera platsdelning i din webbläsare och ladda om sidan.
                    </div>
                </div>
            )}

            {/* Fetch error */}
            {fetchError && (
                <div
                    className="alert alert-danger mx-auto"
                    style={{ maxWidth: 520 }}
                    role="alert"
                >
                    {fetchError}
                    <div className="mt-2">
                        <button
                            className="btn btn-sm btn-outline-light"
                            onClick={() => window.location.reload()}
                        >
                            Försök igen
                        </button>
                    </div>
                </div>
            )}

            {/* Loading */}
            {isLoading && (
                <div className="text-muted my-4" aria-live="polite">
                    Söker efter restauranger i närheten…
                </div>
            )}

            {/* Empty state */}
            {showEmpty && (
                <div className="text-muted my-4">
                    Inga restauranger finns i närheten.
                </div>
            )}

            {/* Results */}
            <div className="d-flex flex-wrap justify-content-center gap-3">
                {restaurants.map((restaurant) => (
                    <div
                        key={restaurant.slug}
                        className="p-3 border rounded-lg shadow-sm restaurant-card text-center"
                        style={{
                            width: "260px",
                            cursor: "pointer",
                            transition: "transform 0.2s, box-shadow 0.2s",
                        }}
                        onClick={() => navigate(`/${restaurant.slug}`)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                navigate(`/${restaurant.slug}`);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <h5 className="m-0 fw-bold">{restaurant.name}</h5>
                        {restaurant.distance != null && (
                            <div className="text-muted mt-1 small">
                                ~{Math.round(restaurant.distance)} m bort
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
